import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  finleyAssert,
  error as commonError,
} from '@finley-technologies/common';
import { typeguards } from '@finley-technologies/finley-types';

import { useGetAssignmentStrategyQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import type {
  EligibilityExclusion,
  LoanProductCriteria,
  TotalAssignableExposureHeuristicConfig,
} from '../types';
import { AssignmentStrategyForm } from './assignmentStrategyForm/AssignmentStrategyForm';

export const AssignmentEditStrategyContainer = ({
  organizationId,
  readOnly,
}: {
  organizationId: string;
  readOnly: boolean;
}) => {
  const history = useHistory();

  const [strategyName, setStrategyName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [initialLoanProductCriteria, setInitialLoanProductCriteria] = useState<
    LoanProductCriteria[]
  >([]);
  const [
    initialEligibilityExclusions,
    setInitialEligibilityExclusions,
  ] = useState<EligibilityExclusion[]>([]);
  const [
    decisionHeuristicConfig,
    setDecisionHeuristicConfig,
  ] = useState<TotalAssignableExposureHeuristicConfig>();

  const { strategyId } = useParams<{
    strategyId: string;
  }>();

  const {
    data,
    loading: assignmentStrategyLoading,
    refetch: refetchAssignmentStrategy,
  } = useGetAssignmentStrategyQuery({
    variables: { strategyId },
    fetchPolicy: 'network-only',
    onError: () => {
      history.push(
        readOnly ? '/assignments/strategy' : '/settings/assignment-strategy',
      );
    },
  });

  useEffect(() => {
    if (data) {
      setStrategyName(data.assignmentStrategy.name);
      setDescription(data.assignmentStrategy.description ?? '');
      setInitialLoanProductCriteria(
        data.assignmentStrategy.loanProductCriteria,
      );
      setInitialEligibilityExclusions(
        data.assignmentStrategy.eligibilityExclusions,
      );
      const heuristicConfig = JSON.parse(
        data.assignmentStrategy.decisionHeuristic,
      );
      finleyAssert(
        typeguards.assignment.isDecisionHeuristicConfig(heuristicConfig),
        new commonError.TypeError({
          resourceType: 'DecisionHeuristicConfig',
          typeguardName: 'isDecisionHeuristicConfig',
          metadata: {
            logMeta: {
              assignmentStrategyId: strategyId,
              heuristicConfig,
            },
          },
        }),
      );
      setDecisionHeuristicConfig(heuristicConfig.config);
    }
  }, [data, strategyId]);

  return (
    <AssignmentStrategyForm
      organizationId={organizationId}
      strategyId={strategyId}
      initialStrategyName={strategyName}
      initialDescription={description}
      initialLoanProductCriteria={initialLoanProductCriteria}
      initialEligibilityExclusions={initialEligibilityExclusions}
      initialDecisionHeuristicConfig={decisionHeuristicConfig}
      getStrategyLoading={assignmentStrategyLoading}
      refetchAssignmentStrategy={refetchAssignmentStrategy}
      backUrl={
        readOnly ? '/assignments/strategy' : '/settings/assignment-strategy'
      }
      readOnly={readOnly}
    />
  );
};
